import { createApi } from '@reduxjs/toolkit/dist/query/react'
import baseQueryWithOnlineCourse from '../../app/baseQueryWithOnlineCourse'
import {
    ActiveCourseTopicState,
    PurchasedCourseTopicState,
    TopicState,
    neighbourPositions,
} from '../types'

interface CourseDetailParams {
    courseSubjectId: number
    language: string
    coursePriceId: number
}

interface ResreshTopicOrderParams {
    id: number
    neighbourPositions: neighbourPositions
}

export const courseTopicApiSlice = createApi({
    reducerPath: 'courseTopicApi',
    baseQuery: baseQueryWithOnlineCourse,
    tagTypes: ['ActiveCourseTopic', 'InactiveCourseTopic'],
    endpoints: (build) => ({
        fetchActiveCourseTopics: build.query<ActiveCourseTopicState[], number>({
            query: (id) => ({
                url: `course-topic/active/${id}`,
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ courseTopicId }) => ({
                              type: 'ActiveCourseTopic' as const,
                              courseTopicId,
                          })),
                          { type: 'ActiveCourseTopic', id: 'LIST' },
                      ]
                    : [{ type: 'ActiveCourseTopic', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),

        fetchInactiveCourseTopics: build.query<TopicState[], number>({
            query: (id) => ({
                url: `course-topic/inactive/${id}`,
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ id }) => ({
                              type: 'InactiveCourseTopic' as const,
                              id,
                          })),
                          { type: 'InactiveCourseTopic', id: 'LIST' },
                      ]
                    : [{ type: 'InactiveCourseTopic', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),

        createCourseTopic: build.mutation<any, any>({
            query: (courseTopic) => ({
                url: 'course-topic',
                method: 'POST',
                body: courseTopic,
            }),
            invalidatesTags: [
                { type: 'ActiveCourseTopic', id: 'LIST' },
                { type: 'InactiveCourseTopic', id: 'LIST' },
            ],
        }),

        deleteCourseTopic: build.mutation<void, number>({
            query: (id) => ({
                url: `/course-topic/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: [
                { type: 'ActiveCourseTopic', id: 'LIST' },
                { type: 'InactiveCourseTopic', id: 'LIST' },
            ],
        }),

        refreshCourseTopicsOrder: build.mutation<void, ResreshTopicOrderParams>(
            {
                query: ({ id, neighbourPositions }) => ({
                    url: `course-topic/arrange/${id}`,
                    method: 'PUT',
                    body: neighbourPositions,
                }),
                invalidatesTags: [{ type: 'ActiveCourseTopic', id: 'LIST' }],
            },
        ),

        getPurchaseCourseTopics: build.query<
            PurchasedCourseTopicState[],
            CourseDetailParams
        >({
            query: ({ courseSubjectId, language, coursePriceId }) => ({
                url: `purchase/course-topic/${courseSubjectId}`,
                params: {
                    language,
                    coursePriceId,
                },
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ courseTopicId }) => ({
                              type: 'ActiveCourseTopic' as const,
                              courseTopicId,
                          })),
                          { type: 'ActiveCourseTopic', id: 'LIST' },
                      ]
                    : [{ type: 'ActiveCourseTopic', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),
    }),
})
